import { Injectable } from '@angular/core';

import { of } from 'rxjs';
import { catchError, filter, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';

import { LoggerService } from '../../../core/services/logger.service';
import { SnackBarService } from '../../../core/services/snackbar.service';
import * as uiActions from '../../../modules/shared/store/ui.actions';
import { ChannelConsent, ChannelStatus } from '../interfaces/IChannel';
import { ChannelsService } from '../services/channels.service';
import { LogsService } from '../services/logs.service';
import { MessagesService } from '../services/messages.service';
import { MessagingApiService } from '../services/messaging-api/messaging-api.service';
import { PermissionsService } from '../services/permissions.service';
import { SoundsService } from '../services/sounds.service';
import { TranslaterService } from '../services/translater.service';
import * as chatActions from './chat.actions';
import { AppStateWithChat } from './chat.reducer';
import { ChatEffectsBase } from './chat-effects-base';

@Injectable()
export class MessagesEffects extends ChatEffectsBase {
  constructor(
    actions$: Actions,
    store: Store<AppStateWithChat>,
    messagingApiService: MessagingApiService,
    snackBarService: SnackBarService,
    channelsService: ChannelsService,
    messagesService: MessagesService,
    permissionsService: PermissionsService,
    logger: LoggerService,
    private soundsService: SoundsService,
    private translaterService: TranslaterService,
    private logsService: LogsService
  ) {
    super(
      actions$,
      store,
      messagingApiService,
      snackBarService,
      channelsService,
      messagesService,
      permissionsService,
      logger
    );
  }

  setMessageReaction$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(chatActions.setMessageReaction),
        mergeMap(({ reaction, channelUID, messageUID }) => {
          return this.messagingApiService.sendReaction(reaction, channelUID, messageUID).pipe(
            mergeMap(() => {
              return [];
            }),
            catchError((error) => {
              this.logger.handleEffectError(error);
              return [];
            })
          );
        })
      ),
    { dispatch: false }
  );

  sendMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(chatActions.sendMessage),
      withLatestFrom(
        this.store.select((state) => {
          return state.chat.selectedChannel;
        })
      ),
      filter(([, selectedChannel]) => selectedChannel !== null),
      mergeMap(([action, selectedChannel]) => {
        let newMessage = this.messagesService.createMessage(
          action.message ?? '',
          action.attachments,
          selectedChannel!.messagesTags ?? []
        );
        newMessage = this.messagesService.setMessagesOwnership([newMessage], this.users)[0];
        const channel = selectedChannel!;
        this.store.dispatch(
          chatActions.setLastMessage({
            message: newMessage,
            channel,
          })
        );
        this.store.dispatch(chatActions.setSmartReplies({ smartReplies: [], channelUID: channel.uid }));
        this.store.dispatch(uiActions.scrollToPosition({ value: this.scrollPosition }));
        return this.messagingApiService
          .sendMessage({
            message: newMessage.body,
            channel,
            attachments: newMessage.attachments ?? [],
            systemTags: newMessage.systemTags,
            template: action.template ?? undefined,
          })
          .pipe(
            mergeMap((res) => {
              const ownerId = this.user?.subjectMessageableEntity?.businessReference?.entityUID;
              const ownerName = this.user?.subjectMessageableEntity?.displayName;
              if (ownerId && ownerName) {
                this.channelsService.changeChannelOwner(channel, ownerId, ownerName);
              }
              this.channelsService.updateChannelOrderID(channel);
              this.soundsService.playSentMessage();
              if (action.log) this.logsService.sendLogProfessionalizer(action.log, res.uid);
              this.channelsService.changeChannelStatus(channel, ChannelStatus.RESPONDED);
              return [
                chatActions.setMessageDeliveryStatus({
                  messageUID: newMessage.uid,
                  newMessageUID: res.uid,
                  status: 'sent',
                  channelUID: channel.uid,
                  attachments: res.attachments,
                  body: res.body,
                }),
              ];
            }),
            catchError((error: { status: number; errorDescription: string; isUploadError?: boolean }) => {
              if (error.isUploadError) {
                return [
                  chatActions.setMessageDeliveryStatus({
                    channelUID: channel.uid,
                    messageUID: newMessage.uid,
                    status: 'failed',
                    attachments: newMessage.attachments,
                    errorMessage: this.messagesService.getErrorMessage(newMessage),
                  }),
                ];
              }
              switch (error.status) {
                case 403:
                  return [
                    chatActions.setChannelStatusEnabled({ value: false, channelUID: channel.uid }),
                    chatActions.setChannelConsent({
                      value: ChannelConsent.REACTIVATE as ChannelConsent,
                      channelUID: channel.uid,
                    }),
                    chatActions.getMessagesTemplates({ channel: { ...channel, enabled: false } }),
                  ];
                case 499:
                  return [
                    chatActions.changeOPTOut({ optOut: false }),
                    chatActions.unsetMessage({ messageUid: newMessage.uid, channelUid: channel.uid }),
                  ];
                default:
                  return [
                    chatActions.setMessageDeliveryStatus({
                      channelUID: channel.uid,
                      messageUID: newMessage.uid,
                      status: 'failed',
                      attachments: newMessage.attachments,
                      errorMessage: this.messagesService.getErrorMessage(newMessage),
                    }),
                  ];
              }
            })
          );
      })
    )
  );

  getMessages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(chatActions.getMessages),
      switchMap((action) => {
        return this.messagingApiService.getMessages(action.channel).pipe(
          mergeMap((data) => {
            if (!data.documents.length) {
              this.store.dispatch(chatActions.setCompletedMessagesChannel({ channelUID: action.channel.uid, value: true }));
            }
            const messages = this.messagesService.setMessagesOwnership(data.documents, this.users);
            return [
              chatActions.setMessages({
                messages: messages,
                concat: action.concat ?? false,
                currentChannel: action.channel,
              }),
              chatActions.setLoadingOldMessages({ channelUID: action.channel.uid, value: false }),
            ];
          }),
          catchError((error) => {
            this.logger.handleEffectError(error);
            this.store.dispatch(chatActions.setCompletedMessagesChannel({ channelUID: action.channel.uid, value: true }));
            this.snackBarService.showErrorSnackBar('Failed to get messages, please try again');
            return [chatActions.setLoadingOldMessages({ channelUID: action.channel.uid, value: false })];
          })
        );
      })
    )
  );

  getMessagesByTagMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(chatActions.getMessagesByTagMessage),
      switchMap((action) => {
        const channel = {
          ...action.channel,
          messages: action.message
            ? [{ ...action.message, createdAt: new Date(action.message.createdAt.getTime() + 1) }]
            : [],
        };
        return this.messagingApiService.getMessages(channel).pipe(
          mergeMap((data) => {
            const messages = this.messagesService.setMessagesOwnership(data.documents, this.users);
            return [
              chatActions.checkNewMessagesByDate({ date: action.message?.createdAt, channel: action.channel }),
              chatActions.setMessages({
                messages: messages,
                concat: false,
                currentChannel: action.channel,
              }),
              uiActions.setLoadingChannel({ value: false }),
              uiActions.setLoadingChannels({ value: false }),
            ];
          }),
          catchError((error) => {
            this.logger.handleEffectError(error);
            this.snackBarService.showErrorSnackBar('Failed to get messages, please try again');
            return [
              chatActions.setLoadingOldMessages({ channelUID: action.channel.uid, value: false }),
              uiActions.setLoadingChannel({ value: false }),
              uiActions.setLoadingChannels({ value: false }),
            ];
          })
        );
      })
    )
  );

  checkNewMessagesByDate$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(chatActions.checkNewMessagesByDate),
        switchMap(({ channel, date }) => {
          return this.messagingApiService.checkNewMessagesByDate(channel, date).pipe(
            mergeMap((res) => {
              if (res) {
                this.store.dispatch(chatActions.setChannelMessagesEndDate({ channel, value: date }));
              }
              return [];
            }),
            catchError((error) => {
              this.logger.handleEffectError(error);
              return of();
            })
          );
        })
      ),
    { dispatch: false }
  );

  getMessageTranslation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(chatActions.getMessageTranslation),
      mergeMap(({ message, channel }) => {
        this.store.dispatch(chatActions.setMessageTranslationStatus({ channel, message, status: true, error: false }));
        return this.translaterService.translateText(message.body, this.translaterService.dealerLanguge).pipe(
          mergeMap((translation) => {
            this.store.dispatch(chatActions.setMessageTranslationStatus({ channel, message, status: false, error: false }));
            return [chatActions.setMessageTranslated({ message, translation, channelUID: channel.uid })];
          }),
          catchError((err) => {
            this.logger.handleEffectError(err);
            this.store.dispatch(chatActions.setMessageTranslationStatus({ channel, message, status: false, error: true }));
            return of();
          })
        );
      })
    )
  );

  changeMessageDliveryStatus$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(chatActions.changeMessageDeliveryStatus),
        mergeMap(({ messageUID, status, channelUID }) => {
          this.store.dispatch(
            chatActions.setMessageDeliveryStatus({
              channelUID,
              messageUID,
              status,
            })
          );
          return this.messagingApiService.changeMessageDeliveryStatus(messageUID, status).pipe(
            mergeMap(() => {
              return [];
            }),
            catchError((err) => {
              this.logger.handleEffectError(err);
              return [];
            })
          );
        })
      ),
    { dispatch: false }
  );

  deleteMessagesById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(chatActions.deleteMessagesById),
      withLatestFrom(
        this.store.select((state) => {
          return state.chat.selectedChannel;
        })
      ),
      filter(([, selectedChannel]) => selectedChannel !== null),
      switchMap(([action, selectedChannel]) => {
        const channel = selectedChannel!;
        return this.messagingApiService.deleteMessageById(action.messageUid).pipe(
          mergeMap(() => {
            return [chatActions.unsetMessage({ messageUid: action.messageUid, channelUid: channel.uid })];
          }),
          catchError((err) => {
            this.logger.handleEffectError(err);
            this.snackBarService.showErrorSnackBar('The message could not be deleted, please try again');
            return of(
              chatActions.setMessageDeliveryStatus({
                channelUID: channel.uid,
                messageUID: action.messageUid,
                status: 'delivered',
              })
            );
          })
        );
      })
    )
  );

  resendMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(chatActions.resendMessage),
      withLatestFrom(
        this.store.select((state) => {
          return state.chat.selectedChannel;
        })
      ),
      filter(([, selectedChannel]) => selectedChannel !== null),
      mergeMap(([action, selectedChannel]) => {
        const channel = selectedChannel!;
        this.store.dispatch(
          chatActions.setMessageDeliveryStatus({
            channelUID: channel.uid,
            messageUID: action.message.uid,
            status: 'sending',
          })
        );
        return this.messagingApiService
          .sendMessage({
            message: action.message.body,
            channel,
            attachments: action.message.attachments ?? [],
            systemTags: [action.message.uid],
          })
          .pipe(
            mergeMap((res) => {
              const ownerId = this.user?.subjectMessageableEntity?.businessReference?.entityUID;
              const ownerName = this.user?.subjectMessageableEntity?.displayName;
              if (ownerId && ownerName) {
                this.channelsService.changeChannelOwner(channel, ownerId, ownerName);
              }
              return [
                chatActions.setMessageDeliveryStatus({
                  newMessageUID: res.uid,
                  status: 'sent',
                  messageUID: action.message.uid,
                  channelUID: channel.uid,
                }),
              ];
            }),
            catchError((error: { status: number; errorDescription: string }) => {
              this.logger.handleEffectError(error);
              if (error.status === 499) {
                return [
                  chatActions.changeOPTOut({ optOut: false }),
                  chatActions.unsetMessage({ messageUid: action.message.uid, channelUid: channel.uid }),
                ];
              } else {
                return [
                  chatActions.setMessageDeliveryStatus({
                    channelUID: channel.uid,
                    messageUID: action.message.uid,
                    status: 'failed',
                    errorMessage: this.messagesService.getErrorMessage(action.message),
                  }),
                ];
              }
            })
          );
      })
    )
  );
}
